// Define the PriceRangeSlider as a custom element
class PriceRangeSliderElement extends HTMLElement {
    // Helper method to check if a point is within a rectangle
    isPointInRect(x, y, rect) {
      return (
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom
      );
    }
    constructor() {
      super();
      this.initialized = false;
      
      // Add support for touch events for mobile
      this.touchActive = false;
      this.touchThumb = null;
    }
  
    connectedCallback() {
      // Only initialize once
      if (this.initialized) return;
      this.initialized = true;
      
      // Initialize the slider when the element is connected to the DOM
      this.initSlider();
      
      // Create a MutationObserver to watch for changes to this element's children
      this.observer = new MutationObserver((mutations) => {
        // If the inputs were replaced, we need to reinitialize
        if (mutations.some(mutation => mutation.type === 'childList')) {
          //console.log('Price slider container changed, reinitializing');
          // Clear existing slider elements
          const oldWrapper = this.querySelector('.price-slider-wrapper');
          if (oldWrapper) {
            oldWrapper.remove();
          }
          
          // Reinitialize the slider
          this.initSlider();
        }
      });
      
      // Start observing the element
      this.observer.observe(this, { childList: true, subtree: true });
    }
  
    disconnectedCallback() {
      // Clean up the observer when the element is removed from the DOM
      if (this.observer) {
        this.observer.disconnect();
      }
      
      // Remove document event listeners
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
      
      // Remove touch event listeners
      document.removeEventListener('touchmove', this.handleTouchMove);
      document.removeEventListener('touchend', this.handleTouchEnd);
      document.removeEventListener('touchcancel', this.handleTouchEnd);
    }
    
    initSlider() {
      // Determine if this is a mobile slider
      const isMobile = this.hasAttribute('data-mobile') || 
                      this.closest('.mobile-facets__submenu') ||
                      this.closest('[id^="FacetMobile-"]');
      
      // Find the input fields with more flexible selectors that work for both desktop and mobile
      const minInput = this.querySelector('input[id*="Filter"][id*="GTE"], input[id*="Mobile-Filter"][id*="GTE"], input[name*="min"], input[placeholder*="0"]');
      const maxInput = this.querySelector('input[id*="Filter"][id*="LTE"], input[id*="Mobile-Filter"][id*="LTE"], input[name*="max"], input[placeholder*="2200"]');
      
      if (!minInput || !maxInput) {
        //console.log('Price inputs not found in container:', this);
        return;
      }
      
      //console.log(`Found price inputs (${isMobile ? 'mobile' : 'desktop'}):`, { minInput, maxInput });
      
      // Get min and max values from inputs with fallbacks
      const minValue = parseFloat(minInput.getAttribute('min') || minInput.getAttribute('placeholder') || 0);
      const maxValue = parseFloat(maxInput.getAttribute('max') || maxInput.getAttribute('placeholder') || 2200);
      
      //console.log('Min/Max values:', { minValue, maxValue });
      
      // Create a wrapper for our slider to help with styling
      const sliderWrapper = document.createElement('div');
      sliderWrapper.className = 'price-slider-wrapper';
      sliderWrapper.style.padding = '0px 30px 10px 15px';
      sliderWrapper.style.width = '100%';
      
      // Create slider elements with inline styles for reliability
      const sliderContainer = document.createElement('div');
      sliderContainer.className = 'price-slider-container';
      sliderContainer.style.position = 'relative';
      sliderContainer.style.height = '30px';
      sliderContainer.style.width = '100%';
      
      const track = document.createElement('div');
      track.className = 'price-slider-track';
      track.style.position = 'absolute';
      track.style.height = '0.02rem';
      track.style.backgroundColor = '#252525';
      track.style.borderRadius = '2px';
      track.style.width = '100%';
      track.style.top = '50%';
      track.style.transform = 'translateY(-50%)';
      
      const range = document.createElement('div');
      range.className = 'price-slider-range';
      range.style.position = 'absolute';
      range.style.height = '100%';
      range.style.backgroundColor = '#404040';
      range.style.borderRadius = '2px';
      range.style.top = '0';
      
      const minThumb = document.createElement('div');
      minThumb.className = 'price-slider-thumb min-thumb';
      this.setThumbStyles(minThumb);
      
      const maxThumb = document.createElement('div');
      maxThumb.className = 'price-slider-thumb max-thumb';
      this.setThumbStyles(maxThumb);
      
      // Assemble slider
      track.appendChild(range);
      sliderContainer.appendChild(track);
      sliderContainer.appendChild(minThumb);
      sliderContainer.appendChild(maxThumb);
      sliderWrapper.appendChild(sliderContainer);
      
      // Insert slider at the top of the container
      this.insertBefore(sliderWrapper, this.firstChild);
      
      // Setup event listeners for drag functionality
      let isDraggingMin = false;
      let isDraggingMax = false;
      
      // Store in instance variables to be able to remove them later
      this.isDraggingMin = false;
      this.isDraggingMax = false;
      this.isUpdatingByCode = false;
      this.lastValidMinInput = minInput.value || minValue;
      this.lastValidMaxInput = maxInput.value || maxValue;
      
      this.range = range;
      this.track = track;
      this.minThumb = minThumb;
      this.maxThumb = maxThumb;
      this.minInput = minInput;
      this.maxInput = maxInput;
      this.minValue = minValue;
      this.maxValue = maxValue;
      
      // Define handler functions and store them on the instance for later removal
      this.handleThumbMove = (e, isMinThumb) => {
        const rect = this.track.getBoundingClientRect();
        
        // Make sure we stay within the track bounds
        let percent = Math.min(Math.max(0, (e.clientX - rect.left) / rect.width), 1);
        let value = this.minValue + percent * (this.maxValue - this.minValue);
        
        // Ensure value is within the min and max bounds
        value = Math.max(this.minValue, Math.min(this.maxValue, value));
        
        if (isMinThumb) {
          const maxVal = parseFloat(this.maxInput.value || this.maxValue);
          // Update the min value, making sure it doesn't exceed max
          this.minInput.value = Math.min(Math.round(value), maxVal);
        } else {
          const minVal = parseFloat(this.minInput.value || this.minValue);
          // Update the max value, making sure it's not below min
          this.maxInput.value = Math.max(Math.round(value), minVal);
        }
        
        this.updateSliderPositions();
      };
      
      this.handleMouseMove = (e) => {
        if (this.isDraggingMin) {
          this.handleThumbMove(e, true);
          e.preventDefault();
        } else if (this.isDraggingMax) {
          this.handleThumbMove(e, false);
          e.preventDefault();
        }
      };
      
      this.handleMouseUp = () => {
        if (this.isDraggingMin || this.isDraggingMax) {
          // Set flag to prevent cross-triggering of events
          this.isUpdatingByCode = true;
          
          if (this.isDraggingMin) {
            // Only trigger events for min input
            const minChangeEvent = new Event('change', { bubbles: true });
            minChangeEvent._isTriggeredProgrammatically = true;
            const minInputEvent = new Event('input', { bubbles: true });
            minInputEvent._isTriggeredProgrammatically = true;
            this.minInput.dispatchEvent(minInputEvent);
            this.minInput.dispatchEvent(minChangeEvent);
          } else {
            // Only trigger events for max input
            const maxChangeEvent = new Event('change', { bubbles: true });
            maxChangeEvent._isTriggeredProgrammatically = true;
            const maxInputEvent = new Event('input', { bubbles: true });
            maxInputEvent._isTriggeredProgrammatically = true;
            this.maxInput.dispatchEvent(maxInputEvent);
            this.maxInput.dispatchEvent(maxChangeEvent);
          }
          
          // Reset flags
          this.isDraggingMin = false;
          this.isDraggingMax = false;
          this.isUpdatingByCode = false;
        }
      };
      
      // Setup mouse events
      minThumb.addEventListener('mousedown', (e) => {
        this.isDraggingMin = true;
        e.preventDefault();
        e.stopPropagation();
      });
      
      maxThumb.addEventListener('mousedown', (e) => {
        this.isDraggingMax = true;
        e.preventDefault();
        e.stopPropagation();
      });
      
      // Add touch events for mobile
      this.handleTouchStart = (e) => {
        const touch = e.touches[0];
        const minThumbRect = this.minThumb.getBoundingClientRect();
        const maxThumbRect = this.maxThumb.getBoundingClientRect();
        
        // Determine which thumb is being touched
        if (this.isPointInRect(touch.clientX, touch.clientY, minThumbRect)) {
          this.touchActive = true;
          this.touchThumb = 'min';
          e.preventDefault();
        } else if (this.isPointInRect(touch.clientX, touch.clientY, maxThumbRect)) {
          this.touchActive = true;
          this.touchThumb = 'max';
          e.preventDefault();
        } else {
          // Handle track touches (find closest thumb)
          const trackRect = this.track.getBoundingClientRect();
          if (this.isPointInRect(touch.clientX, touch.clientY, trackRect)) {
            const trackPosition = (touch.clientX - trackRect.left) / trackRect.width;
            const minPos = parseFloat(this.minThumb.style.left) / 100;
            const maxPos = parseFloat(this.maxThumb.style.left) / 100;
            
            if (Math.abs(trackPosition - minPos) <= Math.abs(trackPosition - maxPos)) {
              this.touchActive = true;
              this.touchThumb = 'min';
            } else {
              this.touchActive = true;
              this.touchThumb = 'max';
            }
            
            // Immediately move the selected thumb to the touch position
            this.handleTouchMove(e);
            e.preventDefault();
          }
        }
      };
      
      this.handleTouchMove = (e) => {
        if (!this.touchActive) return;
        
        const touch = e.touches[0];
        const rect = this.track.getBoundingClientRect();
        
        // Calculate position within track
        let percent = Math.min(Math.max(0, (touch.clientX - rect.left) / rect.width), 1);
        let value = this.minValue + percent * (this.maxValue - this.minValue);
        
        // Ensure value is within bounds
        value = Math.max(this.minValue, Math.min(this.maxValue, value));
        
        if (this.touchThumb === 'min') {
          const maxVal = this.maxInput.value === '' ? this.maxValue : parseFloat(this.maxInput.value);
          this.minInput.value = this.formatValue(Math.min(Math.round(value), maxVal));
        } else {
          const minVal = this.minInput.value === '' ? this.minValue : parseFloat(this.minInput.value);
          this.maxInput.value = this.formatValue(Math.max(Math.round(value), minVal));
        }
        
        this.updateSliderPositions();
        e.preventDefault();
      };
      
      this.handleTouchEnd = (e) => {
        if (!this.touchActive) return;
        
        // Trigger proper events based on which thumb was moving
        this.isUpdatingByCode = true;
        
        if (this.touchThumb === 'min') {
          const minChangeEvent = new Event('change', { bubbles: true });
          minChangeEvent._isTriggeredProgrammatically = true;
          const minInputEvent = new Event('input', { bubbles: true });
          minInputEvent._isTriggeredProgrammatically = true;
          this.minInput.dispatchEvent(minInputEvent);
          this.minInput.dispatchEvent(minChangeEvent);
        } else {
          const maxChangeEvent = new Event('change', { bubbles: true });
          maxChangeEvent._isTriggeredProgrammatically = true;
          const maxInputEvent = new Event('input', { bubbles: true });
          maxInputEvent._isTriggeredProgrammatically = true;
          this.maxInput.dispatchEvent(maxInputEvent);
          this.maxInput.dispatchEvent(maxChangeEvent);
        }
        
        // Reset touch state
        this.touchActive = false;
        this.touchThumb = null;
        this.isUpdatingByCode = false;
        
        e.preventDefault();
      };
      
      // Add touch event listeners
      minThumb.addEventListener('touchstart', this.handleTouchStart);
      maxThumb.addEventListener('touchstart', this.handleTouchStart);
      track.addEventListener('touchstart', this.handleTouchStart);
      document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      document.addEventListener('touchend', this.handleTouchEnd);
      document.addEventListener('touchcancel', this.handleTouchEnd);
      
      // Add document-level mouse event listeners
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
      
      // Listen for input changes on min input with IMPROVED HANDLER
      minInput.addEventListener('input', (e) => {
        // Skip if this event was triggered programmatically
        if (this.isUpdatingByCode || e._isTriggeredProgrammatically) return;
        
        // Only enforce numeric input, don't validate against min/max yet
        this.enforceNumericInput(this.minInput);
        
        // Don't update the slider while typing to avoid interfering with the input
        // We'll validate properly on blur
      });
      
      // Handle min input blur for full validation with IMPROVED HANDLER
      minInput.addEventListener('blur', () => {
        // Skip processing if this is part of a programmatic update
        if (this.isUpdatingByCode) return;
        
        // Set flag to prevent other handlers from triggering
        this.isUpdatingByCode = true;
        
        setTimeout(() => {
          if (this.minInput.value === '') {
            // If empty, revert to last valid value
            this.minInput.value = this.lastValidMinInput;
          } else {
            let minVal = parseFloat(this.minInput.value);
            
            // Validate the value is within the allowed range
            if (isNaN(minVal) || minVal < this.minValue) {
              this.minInput.value = this.minValue;
            } else {
              const maxVal = parseFloat(this.maxInput.value || this.maxValue);
              // If min exceeds max, cap min to max value
              if (minVal > maxVal) {
                this.minInput.value = maxVal;
              }
            }
          }
          
          this.lastValidMinInput = this.minInput.value;
          this.updateSliderPositions();
          
          // Reset flag after processing is complete
          this.isUpdatingByCode = false;
          
          // Only trigger events for the min input
          const minChangeEvent = new Event('change', { bubbles: true });
          minChangeEvent._isTriggeredProgrammatically = true;
          const minInputEvent = new Event('input', { bubbles: true });
          minInputEvent._isTriggeredProgrammatically = true;
          this.minInput.dispatchEvent(minInputEvent);
          this.minInput.dispatchEvent(minChangeEvent);
        }, 10);
      });
      
      // Listen for input changes on max input with IMPROVED HANDLER
      maxInput.addEventListener('input', (e) => {
        // Skip if this event was triggered programmatically
        if (this.isUpdatingByCode || e._isTriggeredProgrammatically) return;
        
        // Only enforce numeric input, don't validate against min/max yet
        this.enforceNumericInput(this.maxInput);
        
        // Don't update the slider while typing to avoid interfering with the input
        // We'll validate properly on blur
      });
      
      // Handle max input blur for full validation with IMPROVED HANDLER
      maxInput.addEventListener('blur', () => {
        // Skip processing if this is part of a programmatic update
        if (this.isUpdatingByCode) return;
        
        // Set flag to prevent other handlers from triggering
        this.isUpdatingByCode = true;
        
        setTimeout(() => {
          if (this.maxInput.value === '') {
            // If empty, revert to last valid value
            this.maxInput.value = this.lastValidMaxInput;
          } else {
            let maxVal = parseFloat(this.maxInput.value);
            
            // Validate the value is within the allowed range
            if (isNaN(maxVal) || maxVal > this.maxValue) {
              this.maxInput.value = this.formatValue(this.maxValue);
            } else {
              if (this.minInput.value !== '') {
                const minVal = parseFloat(this.minInput.value);
                // If max is less than min, cap max to min
                if (maxVal < minVal) {
                  this.maxInput.value = this.formatValue(minVal);
                } else {
                  // Format to remove decimals if whole number
                  this.maxInput.value = this.formatValue(maxVal);
                }
              } else {
                // Format to remove decimals if whole number
                this.maxInput.value = this.formatValue(maxVal);
              }
            }
          }
          
          this.lastValidMaxInput = this.maxInput.value || this.formatValue(this.maxValue);
          this.updateSliderPositions();
          
          // Reset flag after processing is complete
          this.isUpdatingByCode = false;
          
          // Only trigger events for the max input
          const maxChangeEvent = new Event('change', { bubbles: true });
          maxChangeEvent._isTriggeredProgrammatically = true;
          const maxInputEvent = new Event('input', { bubbles: true });
          maxInputEvent._isTriggeredProgrammatically = true;
          this.maxInput.dispatchEvent(maxInputEvent);
          this.maxInput.dispatchEvent(maxChangeEvent);
        }, 10);
      });
      
      // Track clicking functionality
      track.addEventListener('click', (e) => {
        // Set flag to prevent cross-triggering of events
        this.isUpdatingByCode = true;
        
        // Determine which thumb to move based on click position
        const rect = this.track.getBoundingClientRect();
        const clickPercent = (e.clientX - rect.left) / rect.width;
        const clickValue = this.minValue + clickPercent * (this.maxValue - this.minValue);
        
        const minVal = parseFloat(this.minInput.value || this.minValue);
        const maxVal = parseFloat(this.maxInput.value || this.maxValue);
        
        // Move the closest thumb to the click position
        if (Math.abs(clickValue - minVal) <= Math.abs(clickValue - maxVal)) {
          // Moving min thumb only
          const newMinValue = Math.round(clickValue);
          // If new min value exceeds current max, cap min to max value
          this.minInput.value = Math.min(newMinValue, maxVal);
          
          // Only trigger events for min input
          const minChangeEvent = new Event('change', { bubbles: true });
          minChangeEvent._isTriggeredProgrammatically = true;
          const minInputEvent = new Event('input', { bubbles: true });
          minInputEvent._isTriggeredProgrammatically = true;
          this.updateSliderPositions();
          this.minInput.dispatchEvent(minInputEvent);
          this.minInput.dispatchEvent(minChangeEvent);
        } else {
          // Moving max thumb only
          const newMaxValue = Math.round(clickValue);
          // If new max value is less than current min, cap max to min
          this.maxInput.value = Math.max(newMaxValue, minVal);
          
          // Only trigger events for max input
          const maxChangeEvent = new Event('change', { bubbles: true });
          maxChangeEvent._isTriggeredProgrammatically = true;
          const maxInputEvent = new Event('input', { bubbles: true });
          maxInputEvent._isTriggeredProgrammatically = true;
          this.updateSliderPositions();
          this.maxInput.dispatchEvent(maxInputEvent);
          this.maxInput.dispatchEvent(maxChangeEvent);
        }
        
        // Reset flag
        this.isUpdatingByCode = false;
      });
      
      // Initialize slider positions
      this.updateSliderPositions();
      
      //console.log('Price slider successfully initialized with proper bounds');
    }
  
    // Helper method to set thumb styles
    setThumbStyles(thumb) {
      thumb.style.position = 'absolute';
      thumb.style.width = '15px';
      thumb.style.height = '15px';
      thumb.style.backgroundColor = '#ffffff';
      thumb.style.border = '1px solid #787878';
      thumb.style.borderRadius = '50%';
      thumb.style.top = '50%';
      thumb.style.transform = 'translate(-50%, -50%)';
      thumb.style.cursor = 'pointer';
      thumb.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
      thumb.style.zIndex = '10';
      
      // Add dot in center
      const dot = document.createElement('div');
      dot.style.position = 'absolute';
      dot.style.width = '6px';
      dot.style.height = '6px';
      dot.style.backgroundColor = '#404040';
      dot.style.borderRadius = '50%';
      dot.style.top = '50%';
      dot.style.left = '50%';
      dot.style.transform = 'translate(-50%, -50%)';
      thumb.appendChild(dot);
    }
  
    // IMPROVED: Helper method to enforce numeric input
    enforceNumericInput(input) {
      // Store the current cursor position
      const start = input.selectionStart;
      const end = input.selectionEnd;
      
      const value = input.value;
      // Allow empty string, numbers, and at most one decimal point
      // Also preserve minus sign at the beginning for negative numbers if needed
      let cleaned = value;
      
      // Remove all characters except digits, decimal point, and leading minus
      cleaned = cleaned.replace(/[^\d.-]/g, '');
      
      // Ensure only one decimal point
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Ensure minus sign is only at the beginning if present
      if (cleaned.indexOf('-') !== -1) {
        const withoutMinus = cleaned.replace(/-/g, '');
        cleaned = '-' + withoutMinus;
      }
      
      // Only update if changed to avoid cursor jumping
      if (cleaned !== value) {
        input.value = cleaned;
        
        // Restore cursor position, adjusting if characters were removed
        // before the cursor position
        const newLength = cleaned.length;
        const oldLength = value.length;
        const cursorAdjustment = newLength - oldLength;
        
        // Set selection range to maintain cursor position
        input.setSelectionRange(
          Math.max(0, start + (start < oldLength ? cursorAdjustment : 0)),
          Math.max(0, end + (end < oldLength ? cursorAdjustment : 0))
        );
      }
    }
    
    // Format value to remove decimal places for whole numbers
    formatValue(value) {
      // If it's a whole number, return without decimal places
      return Number.isInteger(value) ? value.toString() : value.toString();
    }
  
    // Method to update slider positions
    updateSliderPositions() {
      if (!this.range || !this.minThumb || !this.maxThumb) return;
      
      // Get the current values from inputs
      let minVal = parseFloat(this.minInput.value || this.minValue);
      let maxVal = parseFloat(this.maxInput.value || this.maxValue);
      
      // Handle NaN values
      if (isNaN(minVal)) minVal = this.minValue;
      if (isNaN(maxVal)) maxVal = this.maxValue;
      
      // Ensure values are within valid range - but DON'T MODIFY THE INPUTS HERE
      // Just adjust the calculation values for the slider position
      minVal = Math.max(this.minValue, Math.min(maxVal, minVal));
      maxVal = Math.min(this.maxValue, Math.max(minVal, maxVal));
      
      // Calculate percentages, clamping to ensure within 0-100% range
      const minPercent = Math.max(0, Math.min(100, ((minVal - this.minValue) / (this.maxValue - this.minValue)) * 100));
      const maxPercent = Math.max(0, Math.min(100, ((maxVal - this.minValue) / (this.maxValue - this.minValue)) * 100));
      
      // Update slider UI
      this.range.style.left = minPercent + '%';
      this.range.style.width = (maxPercent - minPercent) + '%';
      
      this.minThumb.style.left = minPercent + '%';
      this.maxThumb.style.left = maxPercent + '%';
    }
  }
  
  // Register the custom element
  customElements.define('price-range-slider', PriceRangeSliderElement);
  
  // Initialize existing price-range elements
  document.addEventListener('DOMContentLoaded', () => {
    //console.log('Initializing price range sliders for desktop and mobile');
    
    // Auto-upgrade any price-range elements to use our custom element
    const upgrade = () => {
      const priceRanges = document.querySelectorAll('price-range:not([upgraded])');
      
      if (priceRanges.length) {
        //console.log(`Found ${priceRanges.length} price-range elements to upgrade`);
        
        priceRanges.forEach(priceRange => {
          // Create the new element
          const newElement = document.createElement('price-range-slider');
          
          // Copy attributes
          Array.from(priceRange.attributes).forEach(attr => {
            newElement.setAttribute(attr.name, attr.value);
          });
          
          // Copy the inner HTML
          newElement.innerHTML = priceRange.innerHTML;
          
          // Add a class to identify if this is a mobile price range
          if (priceRange.closest('.mobile-facets__submenu') || 
              priceRange.closest('[id^="FacetMobile-"]') ||
              priceRange.querySelector('[id^="Mobile-Filter-"]')) {
            newElement.setAttribute('data-mobile', 'true');
          }
          
          // Replace the old element with the new one
          priceRange.parentNode.replaceChild(newElement, priceRange);
        });
      }
  
      const loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  
    };
    
    // Run once on load
    upgrade();
    
    // Also set up a MutationObserver to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      // Check if any price-range elements were added
      const shouldUpgrade = mutations.some(mutation => {
        if (mutation.type === 'childList') {
          return Array.from(mutation.addedNodes).some(node => {
            // Check if the node itself is a price-range or contains one
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.tagName.toLowerCase() === 'price-range') {
                return true;
              }
              return node.querySelector('price-range') !== null;
            }
            return false;
          });
        }
        return false;
      });
      
      if (shouldUpgrade) {
        //console.log('DOM changed, checking for new price-range elements');
        upgrade();
      }
    });
    
    // Start observing the entire document
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'aria-expanded', 'style', 'class'] // Watch for attributes that might show/hide elements
    });
  });